import { Link } from "react-router-dom";
import { LegalLayout, type Section } from "./LegalLayout.js";

/*
 * /terms — plain-language Terms of Service.
 *
 * Brand voice: wise friend, sentence case, no exclamation marks, no
 * legalese where it can be avoided. The "not therapy" disclaimer is
 * the single critical clause given that Wise Owl is a self-help
 * surface — flagged with a danger-tinted callout so it can't be
 * skimmed past.
 *
 * Not legal advice. These terms are a reasonable starting point
 * for a pre-launch product; have counsel review before going live
 * commercially.
 */
const SECTIONS: Section[] = [
  {
    id: "what",
    title: "What Wise Owl Talks is",
    body: (
      <>
        <p>
          Wise Owl Talks is a quiet self-help web app for journaling, daily resets,
          mood reflection, and conversations with a wise AI companion. It runs
          at <a href="https://wiseowl-web.pages.dev">wiseowl-web.pages.dev</a>{" "}
          (and, when we move our custom domain over, at wiseowl.me).
        </p>
        <p>
          The brand began as the <strong>Wise Owl Talks</strong> podcast at
          wiseowltalks.com. The app extends that voice into a tool. It is
          made and operated by the people behind the podcast.
        </p>
        <p>
          By using Wise Owl Talks you agree to these terms. They&apos;re written in
          plain English on purpose. If a term is unclear, it&apos;s ours to
          fix — write to us at <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>.
        </p>
      </>
    ),
  },
  {
    id: "account",
    title: "Your account",
    body: (
      <>
        <p>
          You need an account to sign in. We ask for an email and (optionally)
          a name and password. You can sign in with a 6-digit email code or
          with the password you set — both work.
        </p>
        <h3>You agree to</h3>
        <ul>
          <li>Give us a real email you can read.</li>
          <li>Keep your sign-in details to yourself. One account per person.</li>
          <li>Let us know quickly if you suspect someone else has signed in.</li>
        </ul>
        <h3>You must be 16 or older</h3>
        <p>
          Wise Owl Talks is not designed for children. If we learn an account
          belongs to someone under 16, we&apos;ll close it and delete the
          data. See the <Link to="/privacy">Privacy page</Link> for how we
          handle that.
        </p>
      </>
    ),
  },
  {
    id: "use",
    title: "Acceptable use",
    body: (
      <>
        <p>
          Wise Owl Talks is your private space. Please don&apos;t use it for things
          it isn&apos;t for:
        </p>
        <ul>
          <li>Don&apos;t use it to plan harm to yourself or anyone else.</li>
          <li>Don&apos;t use it to send abusive, illegal, or hateful content
            to or about other people.</li>
          <li>Don&apos;t try to break, scrape, or overload the service, or
            use it to train other models without our written permission.</li>
          <li>Don&apos;t impersonate someone else or share an account.</li>
        </ul>
        <p>
          If we see one of these happening we may pause the account and write
          to you. Repeated or serious misuse may end an account. We will
          always tell you why.
        </p>
      </>
    ),
  },
  {
    id: "content",
    title: "Your reflections, your content",
    body: (
      <>
        <p>
          <strong>Anything you write inside Wise Owl Talks belongs to you.</strong>{" "}
          Your reflections, journal entries, mood selections, and conversations
          with the owl are yours. We hold them so we can show them back to
          you and so the AI features can work — that&apos;s it.
        </p>
        <p>
          You grant us a narrow, non-exclusive licence to store and process
          your content only as needed to provide the service you signed up
          for. The licence ends when you delete the content or close your
          account.
        </p>
        <h3>What we never do</h3>
        <ul>
          <li>We don&apos;t sell your reflections.</li>
          <li>We don&apos;t share them with advertisers.</li>
          <li>We don&apos;t use them to train external AI models.</li>
        </ul>
        <p>
          See the <Link to="/privacy">Privacy page</Link> for the full picture
          of what we store and where.
        </p>
      </>
    ),
  },
  {
    id: "ai",
    title: "AI features",
    body: (
      <>
        <p>
          The owl uses AI to reflect back what you share — to notice patterns,
          suggest small actions, and ask gentle questions. It is a tool, not
          a person. It is not always right. Treat its replies the way you
          would treat advice from a friend who&apos;s read a lot of books:
          worth considering, never the final word.
        </p>
        <p>
          We may use a small, anonymized sample of feature usage to make
          the owl wiser over time. <strong>Your reflections themselves are
          never sent to train an external model.</strong>
        </p>
      </>
    ),
  },
  {
    id: "billing",
    title: "Plans and billing",
    body: (
      <>
        <p>
          Wise Owl Talks is currently in early access and free to use. When we
          launch paid plans we&apos;ll tell every existing user by email
          before charging. You&apos;ll be able to keep using a free tier
          for the basics.
        </p>
        <p>
          When billing exists: subscriptions renew automatically until you
          cancel. You can cancel anytime from <Link to="/profile">your profile</Link>{" "}
          — no phone call, no forms. Cancellations take effect at the end
          of the current billing period.
        </p>
      </>
    ),
  },
  {
    id: "not-therapy",
    title: "Important — this is not therapy",
    body: (
      <>
        <div className="callout danger">
          <div className="label">Read this once</div>
          <p>
            Wise Owl Talks is a companion app. It is not therapy, not a doctor,
            not a clinician, and not a crisis service. Nothing inside it is
            medical advice.
          </p>
        </div>
        <p>
          If you&apos;re in a crisis or thinking about hurting yourself,
          please contact a real human urgently:
        </p>
        <ul>
          <li>
            <strong>United States · 988</strong> — Suicide and Crisis Lifeline,
            24/7.
          </li>
          <li>
            <strong>United Kingdom · 116 123</strong> — Samaritans, 24/7.
          </li>
          <li>
            <strong>International · </strong>
            <a href="https://findahelpline.com" target="_blank" rel="noreferrer">findahelpline.com</a> lists
            warmlines and hotlines by country.
          </li>
          <li>Or go to your nearest emergency department.</li>
        </ul>
        <p>
          The owl will quietly remind you of this if a reflection touches on
          something heavy. Please listen to that reminder.
        </p>
      </>
    ),
  },
  {
    id: "ending",
    title: "Ending your account",
    body: (
      <>
        <p>
          You can pause or delete your account at any time from{" "}
          <Link to="/profile">Profile → Privacy</Link>. A pause is reversible;
          your data sits idle and nothing is shown to anyone. A delete is
          also reversible for 30 days — after that the data is gone and we
          cannot get it back.
        </p>
        <p>
          We may close an account if you violate these terms repeatedly,
          or if we&apos;re required to by law. We&apos;ll write to you when
          we do, unless the law tells us not to.
        </p>
        <p>
          If we shut Wise Owl Talks down as a service, we&apos;ll give you at
          least 30 days&apos; notice and a way to export your reflections.
        </p>
      </>
    ),
  },
  {
    id: "warranty",
    title: "Warranty and liability",
    body: (
      <>
        <p>
          We work hard to keep Wise Owl Talks calm and dependable, but the service
          is provided <strong>as is</strong> and without warranties of any
          kind. We don&apos;t guarantee it will be error-free or available
          at all times.
        </p>
        <p>
          To the maximum extent allowed by law, our total liability for any
          claim related to the service is limited to the amount you paid us
          in the 12 months before the claim (which, today, is zero). We
          aren&apos;t liable for indirect, incidental, or consequential
          damages.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to these terms",
    body: (
      <>
        <p>
          We might update these terms — usually to add detail or fix
          something. When we make a meaningful change, we&apos;ll show a
          notice inside the app and update the &ldquo;last updated&rdquo;
          date at the top of this page.
        </p>
        <p>
          If you keep using Wise Owl Talks after the change, you accept the new
          terms. If you don&apos;t, you can close your account at any time.
        </p>
      </>
    ),
  },
  {
    id: "law",
    title: "Disputes and law",
    body: (
      <>
        <p>
          These terms are governed by the laws of the United States and,
          where applicable, the State of California. We&apos;d like to
          settle any disagreement directly first — write to{" "}
          <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a> and we&apos;ll
          reply.
        </p>
        <p>
          If a court decides any part of these terms is unenforceable, the
          rest still applies.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          A real person reads everything sent to{" "}
          <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a>. We aim to
          reply within two working days.
        </p>
        <p className="reflect">&ldquo;A quiet inbox is still a kind inbox.&rdquo;</p>
      </>
    ),
  },
];

export const Terms = () => (
  <LegalLayout
    page="terms"
    eyebrow="Terms of service"
    title={<>Plain terms.</>}
    lede="What you and Wise Owl Talks agree to when you use the app. Plain English wherever it works."
    updatedISO="2026-05-21"
    sections={SECTIONS}
  />
);
